import { getBaseURL, getClient } from '../BLL/clients/HttpClient';
import { ApiEndPoint } from '../BLL/Enums/ApiEndPoint';
import { ClaimDetails, ClaimEntity } from '../types';
import {
  FairFareQueryParams,
  getFairFareQueryParams,
} from '../types/interfaces/shared';
import {
  BillVerdictRequest,
  ClaimsStats,
  ClaimWithBills,
} from '../pages/Claims/ClaimDetailsTypes';

export interface ClaimsPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface ClaimsResponse {
  claims: ClaimWithBills[];
  pagination: ClaimsPagination;
}

export const getAllClaims = async (
  query: FairFareQueryParams = {},
  carrierId?: number // Was currentTenant
): Promise<ClaimsResponse> => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });

  const params = getFairFareQueryParams(query);
  if (carrierId) {
    params.carrierId = carrierId;
  }
  const { data } = await client.get<ClaimsResponse>('v1/claims', {
    params,
  });

  return data;
};

export const approveClaim = async (claimId: string | number) => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });
  await client.put(
    `v1/claims/${claimId}/approve`,
    {},
    { validateStatus: (status) => status < 500 }
  );
};

export const fetchClaimsByPolicyNumber = async (
  policyNumber: string | number
) => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });
  const { data } = await client.get<ClaimEntity[]>(`v1/claims/${policyNumber}`);
  return data;
};

export const getClaimDetails = async (claimId: string | number) => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });
  const { data } = await client.get<ClaimDetails>(
    `v1/claims/${claimId}/details`
  );
  return data;
};

export const approveBill = async (billId: number) => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });
  await client.put(
    `v1/bills/${billId}/approve`,
    {},
    { validateStatus: (status) => status < 500 }
  );
};

export const rejectBill = async (billId: number) => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });
  await client.put(
    `v1/bills/${billId}/reject`,
    {},
    { validateStatus: (status) => status < 500 }
  );
};

// New unified API method for bill verdicts
export const submitBillVerdict = async (
  claimId: string,
  verdictData: BillVerdictRequest
) => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });
  await client.put(`v1/claims/${claimId}/verdict`, verdictData, {
    validateStatus: (status) => status < 400,
  });
};

export const getClaimsStats = async (
  period?: number | string,
  carrierId?: number // Was currentTenant
): Promise<ClaimsStats> => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });

  const params: any = {};
  if (carrierId) {
    params.carrierId = carrierId;
  }

  if (typeof period === 'number') {
    params.year = period;
  } else if (typeof period === 'string') {
    if (period.startsWith('year-')) {
      params.year = parseInt(period.replace('year-', ''));
    } else {
      params.period = period;
    }
  }

  const { data } = await client.get<ClaimsStats>('v1/claims/stats', {
    params,
  });

  return data;
};

export interface BulkDownloadConfig {
  timeRange: {
    from: Date;
    to: Date;
  };
  downloadAll: boolean;
  downloadOnlyPending: boolean;
  filenameFormat: string;
  groupBy: 'none' | 'day' | 'caseId' | 'policyNumber' | 'billType';
}

export interface BulkDownloadResponse {
  success: boolean;
  downloadUrl: string;
  expiresAt: Date;
  fileName: string;
  fileCount: number;
  totalSize?: string;
  message?: string;
}

export const requestBulkDownload = async (
  config: BulkDownloadConfig
): Promise<BulkDownloadResponse> => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });

  const { data } = await client.post<BulkDownloadResponse>('v1/claims/bulk-download', config);

  return data;
};
